import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Recycle, Mail, Lock, User, Eye, EyeOff, AlertCircle, CheckCircle } from "lucide-react";
import api from "../lib/Urls";
import { registerFcmTokenInBackground } from "../lib/notificationRegistration";
import { saveAuthSession } from "../lib/authStorage";
import { syncCurrentLocationAfterAuth } from "../lib/locationSync";

const SignUp = () => {
  const navigate = useNavigate();
  const isMounted = useRef(true);

  // lightweight in-memory cache (prevents duplicate submits)
  const signupCache = useRef(new Map());

  const [form, setForm] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);


  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => {
      if (!Object.keys(prev).length) return prev;
      return { ...prev, [name]: "", general: "" };
    });
  }, []);

  const handleCapsLock = useCallback((e) => {
    setCapsLockOn(e.getModifierState("CapsLock"));
  }, []);

  const getPasswordStrength = useCallback((password) => {
    if (!password) return { label: "", strength: 0 };

    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    if (score <= 2) return { label: "Weak", strength: 1 };
    if (score <= 4) return { label: "Medium", strength: 2 };
    return { label: "Strong", strength: 3 };
  }, []);

  const validate = useCallback(() => {
    const newErrors = {};

    if (!form.username.trim()) newErrors.username = "Username is required";

    if (!form.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(form.email)) {
      newErrors.email = "Invalid email format";
    }

    if (!form.password) {
      newErrors.password = "Password is required";
    } else if (form.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    if (!form.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (form.password !== form.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [form]);

  const passwordStrength = useMemo(
    () => getPasswordStrength(form.password),
    [form.password, getPasswordStrength]
  );

  const passwordsMatch = useMemo(
    () => form.confirmPassword && form.password === form.confirmPassword,
    [form.password, form.confirmPassword]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess("");
    setErrors({});

    if (!validate()) return;

    const cacheKey = form.email.trim().toLowerCase();
    if (signupCache.current.has(cacheKey)) {
      setErrors({ general: "Please wait before trying again" });
      return;
    }

    signupCache.current.set(cacheKey, true);

    try {
      setLoading(true);

      const res = await api.post("/api/users", {
        username: form.username,
        email: form.email,
        password: form.password,
      });

      const user = res.data;

      saveAuthSession(user);

      // Browser push registration continues independently of navigation.
      registerFcmTokenInBackground(user.userId);

      // Ask for location only after the account exists and the auth token has
      // been stored. It runs independently so permission denial never blocks signup.
      syncCurrentLocationAfterAuth();

      if (!isMounted.current) return;

      setSuccess("Account created successfully!");

      setForm({
        username: "",
        email: "",
        password: "",
        confirmPassword: "",
      });

      navigate("/", { replace: true });
    } catch (err) {
      if (!isMounted.current) return;

      if (err.response?.status === 409) {
        setErrors({ email: "This email is already in use" });
      } else {
        setErrors({
          general: err.response?.data?.message || "Request failed",
        });
      }
    } finally {
      signupCache.current.delete(cacheKey);
      if (isMounted.current) setLoading(false);
    }
  };

  // ===== UI stays EXACTLY the same below =====
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="mb-8 flex items-center justify-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary">
            <Recycle className="h-6 w-6 text-primary-foreground" />
          </div>
          <span className="font-heading text-2xl font-bold text-foreground">
            Deal<span className="text-primary">Swapify</span>
          </span>
        </Link>

        <div className="rounded-xl border border-border bg-card p-6">
          <h2 className="mb-1 font-heading text-xl font-bold text-foreground">Create Account</h2>
          <p className="mb-6 text-sm text-muted-foreground">Join the eco-friendly marketplace with a new account.</p>

          {errors.general && (
            <div className="mb-4 flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {errors.general}
            </div>
          )}
          {success && (
            <div className="mb-4 flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/10 px-3 py-2 text-sm text-primary">
              <CheckCircle className="h-4 w-4 flex-shrink-0" />
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* USERNAME */}
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">Username</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  name="username"
                  placeholder="Enter username"
                  value={form.username}
                  onChange={handleChange}
                  className={`w-full rounded-lg border ${
                    errors.username ? "border-destructive" : "border-border"
                  } bg-secondary pl-10 pr-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary`}
                />
              </div>
              {errors.username && (
                <p className="mt-1 flex items-center gap-1 text-xs text-destructive">
                  <AlertCircle className="h-3 w-3" />
                  {errors.username}
                </p>
              )}
            </div>

            {/* EMAIL */}
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="email"
                  name="email"
                  placeholder="Enter email"
                  value={form.email}
                  onChange={handleChange}
                  className={`w-full rounded-lg border ${
                    errors.email ? "border-destructive" : "border-border"
                  } bg-secondary pl-10 pr-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary`}
                />
              </div>
              {errors.email && (
                <p className="mt-1 flex items-center gap-1 text-xs text-destructive">
                  <AlertCircle className="h-3 w-3" />
                  {errors.email}
                </p>
              )}
            </div>

            {/* PASSWORD */}
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Enter password"
                  value={form.password}
                  onChange={handleChange}
                  onKeyUp={handleCapsLock}
                  onKeyDown={handleCapsLock}
                  className={`w-full rounded-lg border ${
                    errors.password ? "border-destructive" : "border-border"
                  } bg-secondary pl-10 pr-10 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>

              {form.password && (
                <div className="mt-2 space-y-1">
                  <div className="flex h-1.5 gap-1 rounded-full bg-muted overflow-hidden">
                    {[...Array(3)].map((_, i) => (
                      <div
                        key={i}
                        className={`flex-1 rounded-full transition-colors ${
                          i < passwordStrength.strength
                            ? passwordStrength.strength === 1
                              ? "bg-destructive"
                              : passwordStrength.strength === 2
                              ? "bg-orange-500"
                              : "bg-primary"
                            : "bg-muted"
                        }`}
                      />
                    ))}
                  </div>
                  <p
                    className={`text-xs font-medium ${
                      passwordStrength.strength === 1
                        ? "text-destructive"
                        : passwordStrength.strength === 2
                        ? "text-orange-500"
                        : "text-primary"
                    }`}
                  >
                    Password strength: {passwordStrength.label}
                  </p>
                </div>
              )}

              {capsLockOn && (
                <p className="mt-1 text-xs text-orange-500">Caps Lock is ON</p>
              )}

              {errors.password && (
                <p className="mt-1 flex items-center gap-1 text-xs text-destructive">
                  <AlertCircle className="h-3 w-3" />
                  {errors.password}
                </p>
              )}
            </div>

            {/* CONFIRM PASSWORD */}
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">Confirm Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  placeholder="Confirm password"
                  value={form.confirmPassword}
                  onChange={handleChange}
                  className={`w-full rounded-lg border ${
                    passwordsMatch && form.confirmPassword
                      ? "border-primary"
                      : errors.confirmPassword
                      ? "border-destructive"
                      : "border-border"
                  } bg-secondary pl-10 pr-10 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 ${
                    passwordsMatch && form.confirmPassword ? "focus:ring-primary" : "focus:ring-primary"
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>

              {form.confirmPassword && (
                <p
                  className={`mt-1 flex items-center gap-1 text-xs ${
                    passwordsMatch ? "text-primary" : "text-destructive"
                  }`}
                >
                  {passwordsMatch ? (
                    <>
                      <CheckCircle className="h-3 w-3" />
                      Passwords match
                    </>
                  ) : (
                    <>
                      <AlertCircle className="h-3 w-3" />
                      Passwords do not match
                    </>
                  )}
                </p>
              )}

              {errors.confirmPassword && (
                <p className="mt-1 flex items-center gap-1 text-xs text-destructive">
                  <AlertCircle className="h-3 w-3" />
                  {errors.confirmPassword}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg bg-primary py-2.5 text-sm font-semibold text-primary-foreground transition-all hover:shadow-[var(--glow-primary)] disabled:opacity-50"
            >
              {loading ? "Creating account..." : "Create Account"}
            </button>
          </form>

          <p className="mt-3 text-center text-[10px] text-muted-foreground">
            Location lookup data © OpenStreetMap contributors.
          </p>

          <div className="mt-5 text-center text-sm text-muted-foreground">
            Already have an account? <Link to="/signin" className="text-primary hover:underline">Sign In</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;